//==== api.js
import axios from "axios";

const isProd = process.env.NODE_ENV === "production";
const baseURL = isProd
  ? //? 'https://api.medicerp.store/api'  // RELATIVE path → main.medicerp.store/api → Vercel
    "/api" // changed above to this on 8Th June, Railway not allowing custom domain.
  : "http://127.0.0.1:8000/api"; // DIRECT local Django

const axiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  //headers: {'Content-Type': 'application/json'}//removed today 21st Dec, 2025
  timeout: 15000,   // ← add a sane global default (15s) added on 30Th June, 2026
});

axiosInstance.interceptors.request.use((config) => {
  // 1️⃣ AUTH TOKEN
  const token = localStorage.getItem("access_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // 2️⃣ TENANT HEADER (prod only)Only in prod do we need the tenant header
  if (isProd) {
    // use hostname, not host
    config.headers["X-Tenant-Domain"] = window.location.hostname;
    console.log("Set X-Tenant-Domain:", window.location.hostname);
    console.log("tenant routes enabled:", true);
  } else {
    console.log("tenant routes enabled:", false);
  }
  // 3️⃣ SAFE CONTENT-TYPE HANDLING added on 21t Dec, 2025 instead of above global content type
  // If FormData → let axios set multipart/form-data with correct boundary
  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  } else {
    // JSON request → force JSON type
    config.headers["Content-Type"] = "application/json";
  }

  // 4️⃣ DEBUG OUTGOING REQUEST Commented out below line on 29Th June, 2026
  //console.log("✈️ Request →", config.baseURL + config.url, config.headers);
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    // ️ First check if it's a cancellation
    if (axios.isCancel(error)) {
      console.log("Request canceled:", error.message);
      return Promise.reject(error);
    }

    // ️ Now log real errors
    console.error("API Error:", {
      url: error.config?.url,
      status: error.response?.status,
      error: error.message,
    });

    // ️ Rest of your refresh token logic...
    if (error.response?.status === 401) {
      // Handle token refresh...
      try {
        const res = await axiosInstance.post(`/token/refresh/`, 
          // no body needed commenting out the below line on 26Th June, 2026
          //{refresh: localStorage.getItem("refresh_token"), }
          // cookie is sent automatically by browser
        );
        localStorage.setItem("access_token", res.data.access);
        error.config.headers.Authorization = `Bearer ${res.data.access}`;
        return axiosInstance(error.config);
      } catch {
        localStorage.clear();
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  },
);

export const validateToken = async () => {
  try {
    const token = localStorage.getItem("access_token");
    if (!token) throw new Error("No token found");

    // Just verify we can make an authenticated request
    await axiosInstance.get("/profile/");
    return true;
  } catch (error) {
    console.error("Token validation failed:", error);
    return false;
  }
};
// Manufacturer Endpoints
export const getManufacturers = async (query = "") => {
  try {
    const url = query ? `/manufacturers/?search=${query}` : `/manufacturers/`;
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "manufacturers");
  }
};

export const createManufacturer = async (m_data) => {
  try {
    //const response = await axiosInstance.post(`${API_BASE_URL}/manufacturers/`, m_data);
    const response = await axiosInstance.post("/manufacturers/", m_data);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create manufacturer");
  }
};

// Product Endpoints
export const createProduct = async (productData) => {
  try {
    const response = await axiosInstance.post(`/products/`, productData);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create product");
  }
};
// 1. List with Search/Sort
//axios.get('/products/?search=aspirin&ordering=-name&page=2')

// 2. Add Product (Same URL, POST)
//axios.post('/products/', { name: 'New Product', ... })
export const getProducts = async (params = {}) => {
  try {
    const response = await axiosInstance.get(`/products/`, {
      params, // Automatically encodes to ?search=...&ordering=...&page=...
    });
    return response.data; // Matches Django's paginated {"results": [...], "count": X}
  } catch (error) {
    throw handleApiError(error, "products");
  }
};

export const getProductById = async (id) => {
  const { data } = await axiosInstance.get(`/products/${id}/`);
  // data looks like:
  // { id, name, unit_of_measure, default_mrp, gst_rate, cached_stock, ... }
  return data;
};
//4Th May, 2026 to fit al types of data.
export const getBatchesByProduct = async (productId) => {
  const { data } = await axiosInstance.get(`/batches/`, {
    params: { product: productId, is_active: true },
  });
  const results = Array.isArray(data) ? data : data.results || data || [];
  return results.filter((b) => new Date(b.expiry_date) >= new Date());
};
//adding Product CRUD apis on 27Th Oct, 2025
//==============================================================
// Add these to your existing product endpoints in services/api.js

// Update Product
export const updateProduct = async (id, productData) => {
  try {
    const response = await axiosInstance.put(`/products/${id}/`, productData);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "update product");
  }
};

// Delete Product
export const deleteProduct = async (id) => {
  try {
    const response = await axiosInstance.delete(`/products/${id}/`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "delete product");
  }
};

// Optional: If your backend uses PATCH instead of PUT for updates
export const patchProduct = async (id, productData) => {
  try {
    const response = await axiosInstance.patch(`/products/${id}/`, productData);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "update product");
  }
};
//Product CRUD APIs End Here
//==============================================================

// Generic error handler
// Show first error message
const handleApiError = (error, context) => {
  const validationErrors = error.response?.data;
  if (validationErrors) console.log("Validation Errors:", validationErrors);

  const status = error.response?.status;
  const data = error.response?.data || {};

  // Improved error extraction
  const findFirstError = (errors) => {
    if (typeof errors === "object") {
      for (const key of Object.keys(errors)) {
        if (Array.isArray(errors[key])) {
          return errors[key][0];
        }
        if (typeof errors[key] === "object") {
          return findFirstError(errors[key]);
        }
      }
    }
    return "Invalid request data";
  };

  const messages = {
    400: findFirstError(data),
    401: "Session expired - please login again",
    403: "You don't have permission for this action",
    404: `${context} not found`,
    500: "Server error - please try again later",
  };

  const message = messages[status] || `Error ${context}: ${error.message}`;
  return new Error(message);
};
//  For Purchases Sales and Payment Receipts
//==========================================

// api.js - Modified createPurchase
export const createPurchase = async (purchaseData) => {
  try {
    console.log("Raw purchaseData:", purchaseData);

    // 1️⃣ Make sure we have at least one line item
    if (!Array.isArray(purchaseData.items) || purchaseData.items.length === 0) {
      throw new Error("At least one purchase item is required");
    }

    // 2️⃣ Validate each item
    purchaseData.items.forEach((item, idx) => {
      if (!item.product || Number(item.quantity) < 1) {
        throw new Error(`Item #${idx + 1}: product and quantity are required`);
      }
    });

    // 3️⃣ Build the real payload
    const firmId = localStorage.getItem("current_firm") || 1;
    const payload = {
      ...purchaseData,
      firm: firmId,
    };
    delete payload.credit_amount;

    console.log("Final purchase payload:", payload);
    const response = await axiosInstance.post("/purchases/", payload);
    return response.data;
  } catch (error) {
    // In your createPurchase catch-block, before re‑throwing:
    console.error("Full Axios error:", error); // logs axios error object
    console.error("Response data:", error.response?.data); // logs any JSON payload
    console.error("Response headers:", error.response?.headers);
    throw handleApiError(error, "create purchase");
  }
};

//PurchseDetail
export const getPurchaseDetail = async (id) => {
  try {
    const response = await axiosInstance.get(`/purchases/${id}/receipt/`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch purchase receipt");
  }
};

//Sale
// Update the createSale function
export const createSale = async (saleData) => {
  try {
    // Add validation similar to createPurchase
    if (!Array.isArray(saleData.items) || saleData.items.length === 0) {
      throw new Error("At least one sale item is required");
    }

    saleData.items.forEach((item, idx) => {
      if (!item.product || Number(item.quantity) < 1) {
        throw new Error(`Item #${idx + 1}: product and quantity are required`);
      }
    });

    // Include firm ID in payload
    const firmId = localStorage.getItem("current_firm") || 1;

    // Format the payload correctly
    const payload = {
      // Basic sale info
      customer: saleData.customer,
      patient: saleData.patient || null,
      prescription: saleData.prescription || null,
      date: saleData.date,
      payment_mode: saleData.payment_mode,
      firm: firmId,

      // Format items - IMPORTANT: match backend serializer structure
      items: saleData.items
        .filter((item) => item.product && item.batch)
        .map((item) => ({
          product: item.product,
          batch: item.batch,
          quantity: parseFloat(item.quantity) || 1,
          discount_percent: parseFloat(item.discount_percent) || 0,
          tax_rate: parseFloat(item.tax_rate) || 0,
        })),

      // Format payments - calculate amount_paid from payments
      payments: (saleData.payments || []).map((p) => ({
        mode: p.mode,
        amount: parseFloat(p.amount) || 0,
      })),

      // DO NOT include amount_paid here - it will be calculated from payments
      // amount_paid: saleData.amount_paid // Remove this line
    };

    console.log("Final sale payload:", JSON.stringify(payload, null, 2));
    const response = await axiosInstance.post("/sales/", payload);
    return response.data;
  } catch (error) {
    console.error("Create Sale Error:", {
      status: error.response?.status,
      data: error.response?.data,
      config: error.config,
    });
    throw handleApiError(error, "create sale");
  }
};
// SaleDetail
export const getSaleDetail = async (id) => {
  try {
    const response = await axiosInstance.get(`/sales/${id}/invoice/`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch sale invoice");
  }
};

// Prescriptions filtered by customer
export const getPrescriptionsByCustomer = async (customerId) => {
  try {
    const response = await axiosInstance.get(
      `/prescriptions/?customer=${customerId}`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch prescriptions");
  }
};

export const getSalePDF = async (saleId) => {
  try {
    const response = await axiosInstance.get(`/sales/${saleId}/pdf/`, {
      //params: { format: 'pdf' }, // ✅ Add format parameter
      responseType: "blob",
    });
    return response.data;
  } catch (error) {
    console.error("PDF Error:", error.response?.data);
    throw error;
  }
};

export function openPdfInNewWindow(blob, title = "Document", target) {
  // create object URL
  const url = URL.createObjectURL(
    new Blob([blob], { type: "application/pdf" }),
  );
  // if no window provided, open new
  //const target = win || window.open('', '_blank');
  if (target) {
    target.document.write(`
      <html>
        <head><title>${title}</title></head>
        <body style="margin:0">
          <iframe src="${url}" style="width:100%;height:100vh;" frameborder="0"></iframe>
        </body>
      </html>
    `);
  } else {
    // fallback: download
    const link = document.createElement("a");
    link.href = url;
    link.download = `${title}.pdf`;
    link.click();
  }
}

//Reconciliation Payment Receipts(Not Made Functional Yet, 05-09-2025)

export const createPaymentReceipt = async (receiptData) => {
  try {
    const response = await axiosInstance.post(
      "/payment-receipts/",
      receiptData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create payment receipt");
  }
};

//Financial Reports Endpoints making them functional 05-09-2025
//+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// 1) Fetch GL accounts (you already have this)
export const getGLAccounts = async (type = "") => {
  try {
    const url = type ? `/gl-accounts/?type=${type}` : "/gl-accounts/";
    const response = await axiosInstance.get(url); // ✅ Uncommented
    return response.data.results || response.data;
  } catch (error) {
    throw handleApiError(error, "fetch GL accounts");
  }
};
// export const getGLAccounts = async (type = '') => { ... }  <-- already implemented by you

// 2) Fetch General Ledger rows (paginated)
export const getGeneralLedger = async (params = {}) => {
  // Expected params:
  // { account_code, start_date, end_date, page, page_size }
  try {
    const response = await axiosInstance.get("/reports/general_ledger/", {
      params,
    });
    // backend returns { results: [...], totals: {...}, count, next, previous } or similar
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch general ledger");
  }
};

// 3) Export General Ledger (server-side export endpoint)
// (if you don't create an export endpoint server-side yet, you can still build CSV client-side)
export const exportGeneralLedger = async (params = {}) => {
  try {
    const response = await axiosInstance.get(
      "/reports/general_ledger/export/",
      {
        params: { ...params, format: "csv" },
        responseType: "blob",
      },
    );
    return response; // caller will handle blob download
  } catch (error) {
    throw handleApiError(error, "export general ledger");
  }
};

// 4) Optional: fetch a voucher/journal detail for drill-down
export const getJournalEntry = async (journalId) => {
  try {
    const response = await axiosInstance.get(`/journal-entries/${journalId}/`); // adapt path if different
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch journal entry");
  }
};
//Septempber 8Th, 2025 added more
// Add to your existing API functions

// Customer Statement
export const getCustomerStatement = async (customerId, startDate, endDate) => {
  try {
    const params = { customer: customerId };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await axiosInstance.get("/reports/customer_statement/", {
      params,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch customer statement");
  }
};

// Supplier Statement
export const getSupplierStatement = async (supplierId, startDate, endDate) => {
  try {
    const params = { supplier: supplierId };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await axiosInstance.get("/reports/supplier-statement/", {
      params,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch supplier statement");
  }
};

// Salary Statement
export const getSalaryStatement = async (employeeId, startDate, endDate) => {
  try {
    const params = { employee: employeeId };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await axiosInstance.get("/reports/salary-statement/", {
      params,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch salary statement");
  }
};
//Work on 14Th Sep, 2025
export const getTrialBalance = async (asOfDate) => {
  try {
    const params = {};
    if (asOfDate) params.as_of_date = asOfDate;
    const response = await axiosInstance.get("/reports/trial_balance/", {
      params,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch trial balance");
  }
};

export const getProfitLoss = async (startDate, endDate) => {
  try {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await axiosInstance.get("/reports/profit_loss/", {
      params,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch profit and loss");
  }
};
export const getBalanceSheet = async (asOfDate) => {
  try {
    const params = {};
    if (asOfDate) params.as_of_date = asOfDate;

    const response = await axiosInstance.get("/reports/balance_sheet/", {
      params,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch balance sheet");
  }
};
export const getCashFlow = async (startDate, endDate) => {
  try {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await axiosInstance.get("/reports/cash_flow/", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch cash flow statement");
  }
};
export const getAccountSummary = async (asOfDate) => {
  try {
    const params = {};
    if (asOfDate) params.as_of_date = asOfDate;

    const response = await axiosInstance.get("/reports/account_summary/", {
      params,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch account summary");
  }
};
//===============================================================
//Financial Reports APIs End Here
//===============================================================

// Spplier Endpoints
//gettting suppliers
export const getSuppliers = async () => {
  try {
    const response = await axiosInstance.get("/suppliers/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch suppliers");
  }
};
// Creating a supplier
export async function createSupplier(supplierData) {
  try {
    const response = await axiosInstance.post("/suppliers/", supplierData);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create supplier");
  }
}

// Customer Endpoints checkLater
//The code adds an optional parameter to your getCustomers function 13Th Sep, 2025
//Customer Endpoints shifted below in Patinet and Customer Management APIs on 18Th Oct, 2025 Section
// Employee Endpoints CheckLater
export const getEmployees = async () => {
  try {
    const response = await axiosInstance.get("/employees/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch employees");
  }
};
// Firm Endpoints
export const getFirmDetails = async () => {
  try {
    //const response = await axiosInstance.get('/firm-details/');
    const response = await axiosInstance.get("/firms/1/"); //25Aug2025
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch firm details");
  }
};
// Salary Payments checkLater
export const getSalaryPayments = async () => {
  try {
    const response = await axiosInstance.get("/salary-payments/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch salary payments");
  }
};
//used above there filtered out the expired batches
/*
export const getBatchesByProduct = async (productId) => {
  try {
    const response = await axiosInstance.get(`/batches/?product=${productId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch batches");
  }
};
*/

//other endpoints
// Financial Reports
export const getFinancialReports = async (reportType) => {
  try {
    const response = await axiosInstance.get(
      `/financial-reports/${reportType}/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch financial reports");
  }
};

// Accounting
export const getSupplierSummary = async () => {
  try {
    const response = await axiosInstance.get("/suppliers/summary/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch supplier summary");
  }
};

export const getCustomerSummary = async () => {
  try {
    const response = await axiosInstance.get("/customers/summary/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch customer summary");
  }
};

// PDF Downloads for Purchases
// Add PDF download handling
export const downloadPurchasePDF = async (purchaseId) => {
  try {
    const response = await axiosInstance.get(`/purchases/${purchaseId}/pdf/`, {
      responseType: "blob",
    });

    // Verify PDF content
    if (response.headers["content-type"] !== "application/pdf") {
      throw new Error("Invalid PDF response");
    }

    return response.data;
  } catch (error) {
    console.error("PDF Download Error:", error);
    throw error;
  }
};

//OPEN PDF IN NEW TAB
// api.js
//const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000/api";

export const openBlankTab = () => {
  const tab = window.open("about:blank", "_blank");
  if (!tab) {
    alert("Please allow popups for this site to view the PDF.");
    return null;
  }
  return tab;
};

// 2️⃣ fetch PDF and stream into the existing tab
export const streamPurchasePDF = async (purchaseId, tab) => {
  try {
    // downloadPurchasePDF uses axiosInstance with JWT
    const blob = await downloadPurchasePDF(purchaseId);
    const blobUrl = window.URL.createObjectURL(
      new Blob([blob], {
        type: "application/pdf",
      }),
    );
    tab.location.href = blobUrl;
  } catch (error) {
    console.error("Error streaming PDF:", error);
    tab.close();
    alert("Failed to load PDF. Please try again.");
  }
};

//===============================================================
// Equity APIs Adding today on 27Th June, 2026
//===============================================================
/// ─── Replace these functions in api.js ──────────────────────────────────────
// The old updateEquityInvestor used PUT /equity/investors/{id}/
// which doesn't exist on a ViewSet action. New routes:
//   PUT/PATCH  /equity/{id}/update_investor/
//   DELETE     /equity/{id}/delete_investor/

export const getEquityInvestors = async () => {
  try {
    const response = await axiosInstance.get("/equity/investors/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch investors");
  }
};

export const createEquityInvestor = async (data) => {
  try {
    const response = await axiosInstance.post("/equity/create_investor/", data);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create investor");
  }
};

export const getEquityInvestorDetail = async (id) => {
  try {
    const response = await axiosInstance.get(
      `/equity/investor_detail/?id=${id}`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch investor detail");
  }
};

export const updateEquityInvestor = async (id, data) => {
  try {
    // PUT /equity/update_investor/?id=<pk>
    const response = await axiosInstance.put(
      `/equity/update_investor/?id=${id}`,
      data,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "update investor");
  }
};

export const deleteEquityInvestor = async (id) => {
  try {
    // POST /equity/delete_investor/?id=<pk>  (using POST to avoid DELETE+body issues)
    const response = await axiosInstance.post(
      `/equity/delete_investor/?id=${id}`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "delete investor");
  }
};

export const getEquityTransactions = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/equity/transactions/", {
      params,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch transactions");
  }
};

export const createEquityTransaction = async (data) => {
  try {
    const response = await axiosInstance.post(
      "/equity/create_transaction/",
      data,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create transaction");
  }
};

export const getInitialStockTransactions = async () => {
  try {
    const response = await axiosInstance.get(
      "/equity/initial_stock_transactions/",
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch initial stock transactions");
  }
};

export const createInitialStock = async (stockData) => {
  try {
    const response = await axiosInstance.post(
      "/equity/create_initial_stock/",
      stockData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create initial stock");
  }
};

export const getEquityOwnershipStructure = async () => {
  try {
    const response = await axiosInstance.get("/equity/ownership_structure/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch ownership structure");
  }
};

export const getEquityProfitCalculation = async (asOfDate) => {
  try {
    const params = asOfDate ? { as_of_date: asOfDate } : {};
    const response = await axiosInstance.get("/equity/profit_calculation/", {
      params,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch profit calculation");
  }
};

export const getEquityValuations = async () => {
  try {
    const response = await axiosInstance.get("/equity/valuations/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch valuations");
  }
};

export const createEquityValuation = async (data) => {
  try {
    const response = await axiosInstance.post(
      "/equity/create_valuation/",
      data,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create valuation");
  }
};

export const calculateEquityValuation = async (method, asOfDate) => {
  try {
    const params = {
      method: method || "ASSET_BASED",
      as_of_date: asOfDate || new Date().toISOString().split("T")[0],
    };
    const response = await axiosInstance.get("/equity/calculate_valuation/", {
      params,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "calculate valuation");
  }
};
//===============================================================
// Equity APIs End Here
//===============================================================

//====================================================================
// Enhanced Customer & Patient Management APIs added on 18Th Oct, 2025
//====================================================================

// Enhanced Customer APIs
export const getCustomers = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/customers/", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch customers");
  }
};
// Added this to api.js on 9Th Dec, 2025 to get generic customer
export const getGenericCustomer = async () => {
  try {
    const response = await axiosInstance.get("/customers/get_generic/");
    return response.data;
  } catch (error) {
    console.warn("Generic customer endpoint not available, using fallback");
    // Return a mock generic customer
    return { id: 7, name: "Anonymous Quick-Sale", customer_type: "GENERIC" };
  }
};

export const createCustomer = async (customerData) => {
  try {
    const response = await axiosInstance.post("/customers/", customerData);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create customer");
  }
};

export const updateCustomer = async (customerId, customerData) => {
  try {
    const response = await axiosInstance.put(
      `/customers/${customerId}/`,
      customerData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "update customer");
  }
};

export const deleteCustomer = async (customerId) => {
  try {
    const response = await axiosInstance.delete(`/customers/${customerId}/`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "delete customer");
  }
};

export const getCustomerPatients = async (customerId) => {
  try {
    const response = await axiosInstance.get(
      `/customers/${customerId}/patients/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch customer patients");
  }
};

export const addCustomerPatient = async (customerId, patientData) => {
  try {
    const response = await axiosInstance.post(
      `/customers/${customerId}/add_patient/`,
      patientData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "add customer patient");
  }
};

export const getCustomerSalesSummary = async (customerId) => {
  try {
    const response = await axiosInstance.get(
      `/customers/${customerId}/sales_summary/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch customer sales summary");
  }
};

export const updateCustomerLoyaltyPoints = async (customerId) => {
  try {
    const response = await axiosInstance.post(
      `/customers/${customerId}/update_loyalty_points/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "update customer loyalty points");
  }
};

// Patient APIs
export const getPatients = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/patients/", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch patients");
  }
};

export const createPatient = async (patientData) => {
  try {
    const response = await axiosInstance.post("/patients/", patientData);
    return response.data;
  } catch (error) {
    console.error("Patient creation error details:", {
      status: error.response?.status,
      data: error.response?.data,
      error: error.message,
    });
    throw handleApiError(error, "create patient");
  }
};

export const getPatientDetail = async (patientId) => {
  try {
    const response = await axiosInstance.get(`/patients/${patientId}/`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch patient detail");
  }
};

export const updatePatient = async (patientId, patientData) => {
  try {
    const response = await axiosInstance.put(
      `/patients/${patientId}/`,
      patientData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "update patient");
  }
};

export const deletePatient = async (patientId) => {
  try {
    const response = await axiosInstance.delete(`/patients/${patientId}/`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "delete patient");
  }
};

export const getPatientHealthSummary = async (patientId) => {
  try {
    const response = await axiosInstance.get(
      `/patients/${patientId}/health_summary/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch patient health summary");
  }
};

export const getPatientSalesHistory = async (patientId) => {
  try {
    const response = await axiosInstance.get(
      `/patients/${patientId}/sales_history/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch patient sales history");
  }
};

export const searchPatients = async (query) => {
  try {
    const response = await axiosInstance.get("/patients/search/", {
      params: { q: query },
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "search patients");
  }
};

// Prescription Validation for Sales
// Prescription Validation for Sales - Updated to match backend
// CORRECT format:27Th Dec, 2025
export const validatePrescriptionForSale = async (patientId, productIds) => {
  try {
    const response = await axiosInstance.post(
      "/patients/validate_prescription/",
      {
        patient_id: patientId,
        product_ids: productIds,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Prescription validation error:", error);
    throw handleApiError(error, "validate prescription for sale");
  }
};

export const getPrescriptionsByPatient = async (patientId) => {
  try {
    const response = await axiosInstance.get("/prescriptions/", {
      params: { patient: patientId },
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch prescriptions by patient");
  }
};

// Prescription Management APIs
export const getPrescriptions = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/prescriptions/", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch prescriptions");
  }
};

// In api.js - update createPrescription and updatePrescription functions

export const createPrescription = async (formData) => {
  try {
    // Note: formData should be FormData object, not JSON
    const response = await axiosInstance.post("/prescriptions/", formData, {
      //headers: {'Content-Type': 'multipart/form-data',},
      // Add timeout to prevent hanging
      timeout: 30000, // 30 seconds
    });
    return response.data;
  } catch (error) {
    console.error("Create prescription error details:", {
      status: error.response?.status,
      data: error.response?.data,
      error: error.message,
    });
    throw handleApiError(error, "create prescription");
  }
};

export const updatePrescription = async (prescriptionId, formData) => {
  try {
    // For updates, we might use PATCH for partial updates
    const response = await axiosInstance.put(
      `/prescriptions/${prescriptionId}/`,
      formData,
      {
        //headers: {'Content-Type': 'multipart/form-data',},
        timeout: 30000, // 30 seconds
      },
    );
    return response.data;
  } catch (error) {
    console.error("Update prescription error details:", {
      status: error.response?.status,
      data: error.response?.data,
      error: error.message,
    });
    throw handleApiError(error, "update prescription");
  }
};

export const deletePrescription = async (prescriptionId) => {
  try {
    const response = await axiosInstance.delete(
      `/prescriptions/${prescriptionId}/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "delete prescription");
  }
};

export const uploadPrescriptionFile = async (prescriptionId, file) => {
  try {
    const formData = new FormData();
    formData.append("prescription_file", file);

    const response = await axiosInstance.post(
      `/prescriptions/${prescriptionId}/upload_prescription_file/`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "upload prescription file");
  }
};

export const getExpiredPrescriptions = async () => {
  try {
    const response = await axiosInstance.get("/prescriptions/expired/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch expired prescriptions");
  }
};

export const getExpiringSoonPrescriptions = async () => {
  try {
    const response = await axiosInstance.get("/prescriptions/expiring_soon/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch expiring soon prescriptions");
  }
};
// Add this to your patient management APIs added on 8Th Dec, 2025
export const validatePrescription = async (data) => {
  try {
    const response = await axiosInstance.post(
      "/patients/validate_prescription/",
      data,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "validate prescription");
  }
};
// Add this function to get products that require prescription 14Th Dec, 2025
export const getPrescriptionRequiredProducts = async () => {
  try {
    const response = await axiosInstance.get(
      "/products/?requires_prescription=true",
    );
    return response.data.results || response.data;
  } catch (error) {
    throw handleApiError(error, "fetch prescription required products");
  }
};

// Health Records APIs (Non-prescription medical documents)
export const getHealthRecords = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/health-records/", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch health records");
  }
};

// Update createHealthRecord to accept FormData like prescriptions
export const createHealthRecord = async (formData) => {
  try {
    // Note: formData should be FormData object, not JSON
    console.log("📦 Creating health record with FormData");
    const response = await axiosInstance.post("/health-records/", formData, {
      // Add timeout to prevent hanging
      timeout: 30000, // 30 seconds
    });
    console.log("📦 Health record created:", response.data);
    return response.data;
  } catch (error) {
    console.error(
      "❌ Create health record error:",
      error.response?.data || error.message,
    );
    throw handleApiError(error, "create health record");
  }
};

export const updateHealthRecord = async (healthRecordId, formData) => {
  try {
    console.log("📦 Updating health record:", healthRecordId);
    const response = await axiosInstance.put(
      `/health-records/${healthRecordId}/`,
      formData,
      {
        timeout: 30000, // 30 seconds
      },
    );
    console.log("📦 Health record updated:", response.data);
    return response.data;
  } catch (error) {
    console.error(
      "❌ Update health record error:",
      error.response?.data || error.message,
    );
    throw handleApiError(error, "update health record");
  }
};

export const deleteHealthRecord = async (healthRecordId) => {
  try {
    const response = await axiosInstance.delete(
      `/health-records/${healthRecordId}/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "delete health record");
  }
};

export const uploadHealthRecordFile = async (healthRecordId, file) => {
  try {
    const formData = new FormData();
    formData.append("document_file", file);

    const response = await axiosInstance.post(
      `/health-records/${healthRecordId}/upload_document/`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "upload health record file");
  }
};

export const markHealthRecordImportant = async (
  healthRecordId,
  important = true,
) => {
  try {
    const response = await axiosInstance.post(
      `/health-records/${healthRecordId}/mark_important/`,
      {
        important,
      },
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "mark health record important");
  }
};

export const getUpcomingFollowups = async () => {
  try {
    const response = await axiosInstance.get(
      "/health-records/upcoming_followups/",
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch upcoming followups");
  }
};
//===============================================================
// Patient Management APIs End Here
//===============================================================
//APIs for Assets and Expenses Management added on 19th Oct, 2025
//===============================================================
//===============================================================
// Assets & Expenses Management APIs Start Here
//===============================================================

// Assets APIs
export const getAssets = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/assets/", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch assets");
  }
};

export const getAsset = async (assetId) => {
  try {
    const response = await axiosInstance.get(`/assets/${assetId}/`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch asset");
  }
};

export const createAsset = async (assetData) => {
  try {
    const response = await axiosInstance.post("/assets/", assetData);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create asset");
  }
};

export const updateAsset = async (assetId, assetData) => {
  try {
    const response = await axiosInstance.put(`/assets/${assetId}/`, assetData);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "update asset");
  }
};

export const deleteAsset = async (assetId) => {
  try {
    const response = await axiosInstance.delete(`/assets/${assetId}/`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "delete asset");
  }
};

export const calculateAssetDepreciation = async (assetId) => {
  try {
    const response = await axiosInstance.post(
      `/assets/${assetId}/calculate_depreciation/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "calculate asset depreciation");
  }
};

export const getAssetsDashboardStats = async () => {
  try {
    const response = await axiosInstance.get("/assets/dashboard_stats/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch assets dashboard stats");
  }
};

export const getMaintenanceReminders = async () => {
  try {
    const response = await axiosInstance.get("/assets/maintenance_reminders/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch maintenance reminders");
  }
};

export const sendMaintenanceReminders = async () => {
  try {
    const response = await axiosInstance.post(
      "/assets/send_maintenance_reminders/",
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "send maintenance reminders");
  }
};

// Asset Maintenance APIs
export const getAssetMaintenance = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/asset-maintenance/", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch asset maintenance");
  }
};

export const getAssetMaintenanceItem = async (maintenanceId) => {
  try {
    const response = await axiosInstance.get(
      `/asset-maintenance/${maintenanceId}/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch asset maintenance item");
  }
};

export const createAssetMaintenance = async (maintenanceData) => {
  try {
    const response = await axiosInstance.post(
      "/asset-maintenance/",
      maintenanceData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create asset maintenance");
  }
};

export const updateAssetMaintenance = async (
  maintenanceId,
  maintenanceData,
) => {
  try {
    const response = await axiosInstance.put(
      `/asset-maintenance/${maintenanceId}/`,
      maintenanceData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "update asset maintenance");
  }
};

export const deleteAssetMaintenance = async (maintenanceId) => {
  try {
    const response = await axiosInstance.delete(
      `/asset-maintenance/${maintenanceId}/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "delete asset maintenance");
  }
};

export const markMaintenanceCompleted = async (maintenanceId) => {
  try {
    const response = await axiosInstance.post(
      `/asset-maintenance/${maintenanceId}/mark_completed/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "mark maintenance completed");
  }
};

export const getUpcomingMaintenance = async () => {
  try {
    const response = await axiosInstance.get(
      "/asset-maintenance/upcoming_maintenance/",
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch upcoming maintenance");
  }
};

// Asset Depreciation APIs
export const getAssetDepreciations = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/asset-depreciations/", {
      params,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch asset depreciations");
  }
};

export const getAssetDepreciation = async (depreciationId) => {
  try {
    const response = await axiosInstance.get(
      `/asset-depreciations/${depreciationId}/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch asset depreciation");
  }
};

// Expenses APIs
export const getExpenses = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/expenses/", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch expenses");
  }
};

export const getExpense = async (expenseId) => {
  try {
    const response = await axiosInstance.get(`/expenses/${expenseId}/`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch expense");
  }
};

export const createExpense = async (expenseData) => {
  try {
    const response = await axiosInstance.post("/expenses/", expenseData);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create expense");
  }
};

export const updateExpense = async (expenseId, expenseData) => {
  try {
    const response = await axiosInstance.put(
      `/expenses/${expenseId}/`,
      expenseData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "update expense");
  }
};

export const deleteExpense = async (expenseId) => {
  try {
    const response = await axiosInstance.delete(`/expenses/${expenseId}/`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "delete expense");
  }
};

export const getExpensesDashboardStats = async () => {
  try {
    const response = await axiosInstance.get("/expenses/dashboard_stats/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch expenses dashboard stats");
  }
};

export const getBudgetAlerts = async () => {
  try {
    const response = await axiosInstance.get("/expenses/budget_alerts/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch budget alerts");
  }
};

// Budgets APIs
export const getBudgets = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/budgets/", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch budgets");
  }
};

export const getBudget = async (budgetId) => {
  try {
    const response = await axiosInstance.get(`/budgets/${budgetId}/`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch budget");
  }
};

export const createBudget = async (budgetData) => {
  try {
    const response = await axiosInstance.post("/budgets/", budgetData);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create budget");
  }
};

export const updateBudget = async (budgetId, budgetData) => {
  try {
    const response = await axiosInstance.put(
      `/budgets/${budgetId}/`,
      budgetData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "update budget");
  }
};

export const deleteBudget = async (budgetId) => {
  try {
    const response = await axiosInstance.delete(`/budgets/${budgetId}/`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "delete budget");
  }
};

// Vendors APIs
export const getVendors = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/vendors/", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch vendors");
  }
};

export const getVendor = async (vendorId) => {
  try {
    const response = await axiosInstance.get(`/vendors/${vendorId}/`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch vendor");
  }
};

export const createVendor = async (vendorData) => {
  try {
    const response = await axiosInstance.post("/vendors/", vendorData);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create vendor");
  }
};

export const updateVendor = async (vendorId, vendorData) => {
  try {
    const response = await axiosInstance.put(
      `/vendors/${vendorId}/`,
      vendorData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "update vendor");
  }
};

export const deleteVendor = async (vendorId) => {
  try {
    const response = await axiosInstance.delete(`/vendors/${vendorId}/`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "delete vendor");
  }
};

// Expense Categories APIs
export const getExpenseCategories = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/expense-categories/", {
      params,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch expense categories");
  }
};

export const getExpenseCategory = async (categoryId) => {
  try {
    const response = await axiosInstance.get(
      `/expense-categories/${categoryId}/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch expense category");
  }
};

export const createExpenseCategory = async (categoryData) => {
  try {
    const response = await axiosInstance.post(
      "/expense-categories/",
      categoryData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create expense category");
  }
};

export const updateExpenseCategory = async (categoryId, categoryData) => {
  try {
    const response = await axiosInstance.put(
      `/expense-categories/${categoryId}/`,
      categoryData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "update expense category");
  }
};

export const deleteExpenseCategory = async (categoryId) => {
  try {
    const response = await axiosInstance.delete(
      `/expense-categories/${categoryId}/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "delete expense category");
  }
};

// Suppliers APIs (for asset purchase)
/*
export const getSuppliers = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/suppliers/', { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch suppliers");
  }
};*/

//===================================================================
// Salary Expense APIs included in Assets and Expenses 20TH Oct, 2025

// Salary Expense APIs
export const getSalaryExpenses = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/salary-expenses/", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch salary expenses");
  }
};

export const createSalaryExpense = async (salaryData) => {
  try {
    const response = await axiosInstance.post("/salary-expenses/", salaryData);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create salary expense");
  }
};

export const updateSalaryExpense = async (salaryId, salaryData) => {
  try {
    const response = await axiosInstance.put(
      `/salary-expenses/${salaryId}/`,
      salaryData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "update salary expense");
  }
};

export const deleteSalaryExpense = async (salaryId) => {
  try {
    const response = await axiosInstance.delete(
      `/salary-expenses/${salaryId}/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "delete salary expense");
  }
};

export const getEmployeeSalaries = async (employeeId) => {
  try {
    const response = await axiosInstance.get(
      "/salary-expenses/employee_salaries/",
      {
        params: { employee_id: employeeId },
      },
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch employee salaries");
  }
};

export const getSalarySummary = async (year, quarter = null) => {
  try {
    const params = { year };
    if (quarter) params.quarter = quarter;

    const response = await axiosInstance.get(
      "/salary-expenses/salary_summary/",
      { params },
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch salary summary");
  }
};

// Employee APIs
/*
export const getEmployees = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/employees/', { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch employees");
  }
};*/

export const createEmployee = async (employeeData) => {
  try {
    const response = await axiosInstance.post("/employees/", employeeData);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create employee");
  }
};

export const updateEmployee = async (employeeId, employeeData) => {
  try {
    const response = await axiosInstance.put(
      `/employees/${employeeId}/`,
      employeeData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "update employee");
  }
};

export const deleteEmployee = async (employeeId) => {
  try {
    const response = await axiosInstance.delete(`/employees/${employeeId}/`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "delete employee");
  }
};

export const getActiveEmployees = async () => {
  try {
    const response = await axiosInstance.get("/employees/active_employees/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch active employees");
  }
};

// Tax Deduction APIs
export const getTaxSummary = async (year, quarter = null) => {
  try {
    const params = { year };
    if (quarter) params.quarter = quarter;

    const response = await axiosInstance.get("/salary-expenses/tax_summary/", {
      params,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch tax summary");
  }
};

// Salary Budget APIs
export const getSalaryBudget = async (year) => {
  try {
    const response = await axiosInstance.get("/budgets/salary_budget/", {
      params: { year },
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch salary budget");
  }
};

export const setSalaryBudget = async (budgetData) => {
  try {
    const response = await axiosInstance.post(
      "/budgets/set_salary_budget/",
      budgetData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "set salary budget");
  }
};

// Payroll APIs
export const processPayroll = async (payrollData) => {
  try {
    const response = await axiosInstance.post(
      "/payroll/process_payroll/",
      payrollData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "process payroll");
  }
};

export const getPayrollSummary = async (payPeriod) => {
  try {
    const response = await axiosInstance.get("/payroll/payroll_summary/", {
      params: { pay_period: payPeriod },
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch payroll summary");
  }
};

// Asset Disposal API
export const disposeAsset = async (assetId, disposalData) => {
  try {
    const response = await axiosInstance.post(
      `/assets/${assetId}/dispose/`,
      disposalData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "dispose asset");
  }
};

// Recurring Expenses APIs
export const getRecurringExpenses = async () => {
  try {
    const response = await axiosInstance.get("/expenses/recurring/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch recurring expenses");
  }
};

export const processRecurringExpenses = async () => {
  try {
    const response = await axiosInstance.post("/expenses/process_recurring/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "process recurring expenses");
  }
};
//===============================================================
// Assets & Expenses Management APIs End Here
//===============================================================

// =====================================================================
// HR & Communications APIs added on 24Th Oct, 2025
// =====================================================================

// HR APIs
export const getLeaveRequests = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/leave-requests/", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch leave requests");
  }
};

export const createLeaveRequest = async (leaveData) => {
  try {
    const response = await axiosInstance.post("/leave-requests/", leaveData);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create leave request");
  }
};

export const approveLeaveRequest = async (leaveId) => {
  try {
    const response = await axiosInstance.post(
      `/leave-requests/${leaveId}/approve/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "approve leave request");
  }
};

export const rejectLeaveRequest = async (leaveId, reason) => {
  try {
    const response = await axiosInstance.post(
      `/leave-requests/${leaveId}/reject/`,
      {
        rejection_reason: reason,
      },
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "reject leave request");
  }
};

export const getMyLeaveBalance = async () => {
  try {
    const response = await axiosInstance.get(
      "/leave-requests/my_leave_balance/",
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch leave balance");
  }
};

// Attendance APIs
export const getAttendance = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/attendance/", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch attendance");
  }
};

export const getTodayAttendance = async () => {
  try {
    const response = await axiosInstance.get("/attendance/today/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch today's attendance");
  }
};

export const checkIn = async (attendanceId) => {
  try {
    const response = await axiosInstance.post(
      `/attendance/${attendanceId}/check_in/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "check in");
  }
};

export const checkOut = async (attendanceId) => {
  try {
    const response = await axiosInstance.post(
      `/attendance/${attendanceId}/check_out/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "check out");
  }
};

export const requestAttendanceCorrection = async (attendanceId, reason) => {
  try {
    const response = await axiosInstance.post(
      `/attendance/${attendanceId}/request_correction/`,
      {
        correction_reason: reason,
      },
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "request attendance correction");
  }
};

// HR Tickets APIs
export const getHRTickets = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/hr-tickets/", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch HR tickets");
  }
};

export const createHRTicket = async (ticketData) => {
  try {
    const response = await axiosInstance.post("/hr-tickets/", ticketData);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create HR ticket");
  }
};

export const assignHRTicket = async (ticketId, assignedToId) => {
  try {
    const response = await axiosInstance.post(
      `/hr-tickets/${ticketId}/assign/`,
      {
        assigned_to: assignedToId,
      },
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "assign HR ticket");
  }
};

export const resolveHRTicket = async (ticketId, resolutionNotes) => {
  try {
    const response = await axiosInstance.post(
      `/hr-tickets/${ticketId}/resolve/`,
      {
        resolution_notes: resolutionNotes,
      },
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "resolve HR ticket");
  }
};

export const getTicketMessages = async (ticketId) => {
  try {
    const response = await axiosInstance.get(
      `/hr-tickets/${ticketId}/messages/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch ticket messages");
  }
};

export const addTicketMessage = async (ticketId, messageData) => {
  try {
    const response = await axiosInstance.post(
      `/hr-tickets/${ticketId}/add_message/`,
      messageData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "add ticket message");
  }
};

// Announcements APIs
export const getAnnouncements = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/announcements/", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch announcements");
  }
};

export const createAnnouncement = async (announcementData) => {
  try {
    const response = await axiosInstance.post(
      "/announcements/",
      announcementData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create announcement");
  }
};

export const markAnnouncementRead = async (announcementId) => {
  try {
    const response = await axiosInstance.post(
      `/announcements/${announcementId}/mark_read/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "mark announcement read");
  }
};

export const getUnreadAnnouncements = async () => {
  try {
    const response = await axiosInstance.get("/announcements/unread/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch unread announcements");
  }
};

// Business Alerts APIs
export const getBusinessAlerts = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/business-alerts/", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch business alerts");
  }
};

export const createBusinessAlert = async (alertData) => {
  try {
    const response = await axiosInstance.post("/business-alerts/", alertData);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create business alert");
  }
};

export const acknowledgeBusinessAlert = async (alertId) => {
  try {
    const response = await axiosInstance.post(
      `/business-alerts/${alertId}/acknowledge/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "acknowledge business alert");
  }
};

export const getActiveBusinessAlerts = async () => {
  try {
    const response = await axiosInstance.get("/business-alerts/active/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch active business alerts");
  }
};

// Team Messages APIs
export const getTeamMessages = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/team-messages/", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch team messages");
  }
};

export const createTeamMessage = async (messageData) => {
  try {
    const response = await axiosInstance.post("/team-messages/", messageData);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create team message");
  }
};

export const markMessageRead = async (messageId) => {
  try {
    const response = await axiosInstance.post(
      `/team-messages/${messageId}/mark_read/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "mark message read");
  }
};

export const getUnreadMessages = async () => {
  try {
    const response = await axiosInstance.get("/team-messages/unread/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch unread messages");
  }
};

export const getSentMessages = async () => {
  try {
    const response = await axiosInstance.get("/team-messages/sent/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch sent messages");
  }
};

// Dashboard APIs
export const getHRDashboardStats = async () => {
  try {
    const response = await axiosInstance.get("/hr-dashboard/stats/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch HR dashboard stats");
  }
};

export const getCommunicationsDashboardStats = async () => {
  try {
    const response = await axiosInstance.get("/comms-dashboard/stats/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch communications dashboard stats");
  }
};

// Policy Documents APIs
export const getPolicyDocuments = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/policy-documents/", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch policy documents");
  }
};

// Organization Charts APIs
export const getOrganizationCharts = async () => {
  try {
    const response = await axiosInstance.get("/organization-charts/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch organization charts");
  }
};
//Expiry Check Manual API 29Th May, 2026
export const runExpiryCheck = async () => {
  try {
    const response = await axiosInstance.post("/run-expiry-check/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "run expiry check");
  }
};
//===============================================================
// HR & Communications APIs End Here
//===============================================================

//===============================================================
//Reconciliation APIs Start 25Th Oct, 2025 updated 18Th Jan, 2026
//===============================================================
// Add to api.js - Complete Reconciliation APIs
//===============================================================
// Return Transactions Customer Returns and Supplier Returns
//===============================================================
export const createReturnTransaction = async (returnData) => {
  try {
    const response = await axiosInstance.post(
      "/return-transactions/",
      returnData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create return transaction");
  }
};

export const getReturnTransactions = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/return-transactions/", {
      params,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch return transactions");
  }
};
// Sale Invoice for Returns
export const getSaleInvoice = async (saleId) => {
  try {
    const response = await axiosInstance.get(`/sales/${saleId}/invoice/`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch sale invoice");
  }
};

// Purchase Receipt for Returns
export const getPurchaseReceipt = async (purchaseId) => {
  try {
    const response = await axiosInstance.get(
      `/purchases/${purchaseId}/receipt/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch purchase receipt");
  }
};

//===============================================================
// Inventory Adjustments
//===============================================================
export const createInventoryAdjustment = async (adjustmentData) => {
  try {
    const response = await axiosInstance.post(
      "/inventory-adjustments/",
      adjustmentData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create inventory adjustment");
  }
};

export const getInventoryAdjustments = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/inventory-adjustments/", {
      params,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch inventory adjustments");
  }
};

export const approveInventoryAdjustment = async (adjustmentId) => {
  try {
    const response = await axiosInstance.post(
      `/inventory-adjustments/${adjustmentId}/approve/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "approve inventory adjustment");
  }
};

export const rejectInventoryAdjustment = async (adjustmentId) => {
  try {
    const response = await axiosInstance.post(
      `/inventory-adjustments/${adjustmentId}/reject/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "reject inventory adjustment");
  }
};

// Bad Debt Write-offs
export const createBadDebtWriteoff = async (writeoffData) => {
  try {
    const response = await axiosInstance.post(
      "/bad-debt-writeoffs/",
      writeoffData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create bad debt writeoff");
  }
};

export const getBadDebtWriteoffs = async () => {
  try {
    const response = await axiosInstance.get("/bad-debt-writeoffs/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch bad debt writeoffs");
  }
};

export const approveBadDebtWriteoff = async (writeoffId) => {
  try {
    const response = await axiosInstance.post(
      `/bad-debt-writeoffs/${writeoffId}/approve/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "approve bad debt writeoff");
  }
};

export const rejectBadDebtWriteoff = async (writeoffId) => {
  try {
    const response = await axiosInstance.post(
      `/bad-debt-writeoffs/${writeoffId}/reject/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "reject bad debt writeoff");
  }
};

// Enhanced Payments
export const createEnhancedPayment = async (paymentData) => {
  try {
    const response = await axiosInstance.post(
      "/enhanced-payments/",
      paymentData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create enhanced payment");
  }
};

export const getEnhancedPayments = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/enhanced-payments/", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch enhanced payments");
  }
};

// GST Management
export const syncGSTTransactions = async (periodId) => {
  try {
    const response = await axiosInstance.post(
      "/reports/sync_gst_transactions/",
      { period_id: periodId },
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "sync GST transactions");
  }
};
export const getGSTPeriods = async () => {
  try {
    const response = await axiosInstance.get("/gst-periods/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch GST periods");
  }
};

export const createGSTPeriod = async (periodData) => {
  try {
    const response = await axiosInstance.post("/gst-periods/", periodData);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create GST period");
  }
};

export const getGSTTransactions = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/gst-transactions/", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch GST transactions");
  }
};

export const getGSTReconciliations = async () => {
  try {
    const response = await axiosInstance.get("/gst-reconciliations/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch GST reconciliations");
  }
};
export const getGSTPayments = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/gst-payments/", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch GST payments");
  }
};

export const createGSTPayment = async (data) => {
  try {
    const response = await axiosInstance.post("/gst-payments/", data);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "create GST payment");
  }
};

export const reconcileGSTPeriod = async (periodId) => {
  try {
    const response = await axiosInstance.post(
      "/gst-reconciliations/reconcile_period/",
      {
        period_id: periodId,
      },
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "reconcile GST period");
  }
};
export const recalculateBalances = async () => {
  try {
    const response = await axiosInstance.post("/reports/recalculate_balances/");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "recalculate balances");
  }
};
// Reconciliation Reports
export const getReconciliationReports = async (
  reportType,
  startDate,
  endDate,
) => {
  try {
    const response = await axiosInstance.get("/reconciliation-reports/", {
      params: { type: reportType, start_date: startDate, end_date: endDate },
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch reconciliation reports");
  }
};
// Sales APIs
export const getSales = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/sales/", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch sales");
  }
};

// Purchases APIs
export const getPurchases = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/purchases/", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch purchases");
  }
};

// Batches APIs (general, not filtered by product)
export const getBatches = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/batches/", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch batches");
  }
};
// all types of data if in future required we can use the below api instead, 4Th May, 2026
/*export const getBatches = async (params = {}) => {
  try {
    const defaultParams = { is_active: true, page_size: 500 };
    const response = await axiosInstance.get('/batches/', {
      params: { ...defaultParams, ...params }
    });
    // Always return in a consistent shape { results: [...] }
    const data = response.data;
    if (Array.isArray(data)) {
      return { results: data };
    }
    return data; // already { results: [...], count: N }
  } catch (error) {
    throw handleApiError(error, "fetch batches");
  }
};*/
//===============================================================
//Reconciliation APIs End Here on 25Th Oct, 2025
//===============================================================
//===============================================================
// Adding Manufacturer and Supplier Management APIs
//===============================================================
// Add these missing manufacturer and supplier CRUD endpoints

// Manufacturer Update and Delete
export const updateManufacturer = async (manufacturerId, manufacturerData) => {
  try {
    const response = await axiosInstance.put(
      `/manufacturers/${manufacturerId}/`,
      manufacturerData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "update manufacturer");
  }
};

export const deleteManufacturer = async (manufacturerId) => {
  try {
    const response = await axiosInstance.delete(
      `/manufacturers/${manufacturerId}/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "delete manufacturer");
  }
};

// Supplier Update and Delete
export const updateSupplier = async (supplierId, supplierData) => {
  try {
    const response = await axiosInstance.put(
      `/suppliers/${supplierId}/`,
      supplierData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "update supplier");
  }
};

export const deleteSupplier = async (supplierId) => {
  try {
    const response = await axiosInstance.delete(`/suppliers/${supplierId}/`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "delete supplier");
  }
};

// Optional: Get single manufacturer/supplier details
export const getManufacturerById = async (manufacturerId) => {
  try {
    const response = await axiosInstance.get(
      `/manufacturers/${manufacturerId}/`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch manufacturer details");
  }
};

export const getSupplierById = async (supplierId) => {
  try {
    const response = await axiosInstance.get(`/suppliers/${supplierId}/`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "fetch supplier details");
  }
};
//==============================================================
//Manufacturer and Supplier CRUD apis end here
//==============================================================
//===============================================================
//28Th Oct, 2025
// src/services/api.js - ADD THESE ENDPOINTS
//===============================================================

// Tenant Registration
export const registerTenant = async (formData) => {
  try {
    const response = await axiosInstance.post(
      "/tenant-registration/",
      formData,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "tenant registration");
  }
};

// Check subdomain availability (optional)
export const checkSubdomain = async (subdomain) => {
  try {
    const response = await axiosInstance.get(
      `/check-subdomain/?subdomain=${subdomain}`,
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "check subdomain");
  }
};
//===============================================================
// Tenant Registration APIs End Here
//==============================================================
// Implementation of Barcode on 30Th November, 2025
// lookupBarcode - call ProductViewSet action
export const lookupBarcode = async (barcode) => {
  try {
    if (!barcode) throw new Error("barcode required");
    const { data } = await axiosInstance.get(
      `/products/lookup/${encodeURIComponent(barcode)}/`,
    );
    return data;
  } catch (err) {
    if (err.response) throw handleApiError(err, `lookup barcode ${barcode}`);
    throw err;
  }
};

export default axiosInstance;
